import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { SupportTicket } from '../types';

const SupportResources: React.FC = () => {
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketCategory, setTicketCategory] = useState('');

  const supportChannels = [
    {
      id: '1',
      icon: 'MessageCircle',
      title: 'Live Chat Support',
      description: 'Get instant help from our technical specialists available 24/7',
      availability: 'Available Now',
      responseTime: 'Avg. 2 min response',
      action: 'Start Chat'
    },
    {
      id: '2',
      icon: 'Mail',
      title: 'Email Support',
      description: 'Submit detailed technical questions and receive comprehensive responses',
      availability: '24/7 Support',
      responseTime: 'Within 4 hours',
      action: 'Send Email'
    },
    {
      id: '3',
      icon: 'Phone',
      title: 'Phone Support',
      description: 'Speak directly with implementation experts for complex issues',
      availability: 'Mon-Fri 9AM-6PM EST',
      responseTime: 'Immediate',
      action: 'Call Now'
    },
    {
      id: '4',
      icon: 'Video',
      title: 'Screen Sharing Session',
      description: 'Schedule a remote session for hands-on troubleshooting assistance',
      availability: 'By Appointment',
      responseTime: 'Same day',
      action: 'Schedule Session'
    }
  ];

  const recentTickets: SupportTicket[] = [
    {
      id: 'TKT-2024-001',
      subject: 'Camera stream connection timeout after 30 seconds',
      status: 'in-progress',
      priority: 'high',
      category: 'Technical Issue',
      createdAt: '2024-01-15 10:30 AM',
      lastUpdate: '2024-01-15 2:45 PM'
    },
    {
      id: 'TKT-2024-002',
      subject: 'API rate limit exceeded during peak hours',
      status: 'open',
      priority: 'medium',
      category: 'API Integration',
      createdAt: '2024-01-14 3:20 PM',
      lastUpdate: '2024-01-14 4:15 PM'
    },
    {
      id: 'TKT-2024-003',
      subject: 'Dashboard widget not displaying real-time analytics',
      status: 'resolved',
      priority: 'low',
      category: 'Dashboard',
      createdAt: '2024-01-13 11:00 AM',
      lastUpdate: '2024-01-13 5:30 PM'
    }
  ];

  const categoryOptions = [
    { value: 'technical', label: 'Technical Issue' },
    { value: 'integration', label: 'API Integration' },
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'billing', label: 'Billing & Account' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'other', label: 'Other' }
  ];

  const getStatusColor = (status: SupportTicket['status']) => {
    switch (status) {
      case 'open':
        return 'bg-primary/10 text-primary border-primary/30';
      case 'in-progress':
        return 'bg-warning/10 text-warning border-warning/30';
      case 'resolved':
        return 'bg-success/10 text-success border-success/30';
      case 'closed':
        return 'bg-muted text-text-secondary border-border';
      default:
        return 'bg-muted text-text-secondary border-border';
    }
  };

  const getPriorityColor = (priority: SupportTicket['priority']) => {
    switch (priority) {
      case 'critical':
        return 'text-error';
      case 'high':
        return 'text-warning';
      case 'medium':
        return 'text-primary';
      case 'low':
        return 'text-text-secondary';
      default:
        return 'text-text-secondary';
    }
  };

  return (
    <section className="py-16 px-4 md:px-6 lg:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Support Resources
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Multiple support channels and resources to ensure your success. Our expert team is ready to help you overcome any implementation challenge.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {supportChannels.map((channel) => (
            <div
              key={channel.id}
              className="bg-card rounded-xl border border-border p-6 hover:shadow-brand-lg transition-all duration-300 group"
            >
              <div className="flex flex-col h-full">
                <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4">
                  <Icon name={channel.icon as any} size={24} className="text-primary" />
                </div>
                
                <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
                  {channel.title}
                </h3>
                
                <p className="text-sm text-text-secondary mb-4 flex-grow">
                  {channel.description}
                </p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-xs text-text-secondary">
                    <Icon name="Clock" size={14} />
                    <span>{channel.availability}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-success">
                    <Icon name="Zap" size={14} />
                    <span>{channel.responseTime}</span>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => console.log(`${channel.action} clicked`)}
                >
                  {channel.action}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-card rounded-2xl border border-border p-8">
            <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
              <Icon name="Plus" size={24} className="mr-3" />
              Create Support Ticket
            </h3>
            
            <div className="space-y-4">
              <Input
                label="Subject"
                type="text"
                placeholder="Brief description of your issue"
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                required
              />
              
              <Select
                label="Category"
                placeholder="Select issue category"
                options={categoryOptions}
                value={ticketCategory}
                onChange={setTicketCategory}
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description
                </label>
                <textarea
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={6}
                  placeholder="Provide detailed information about your issue..."
                />
              </div>
              
              <div className="flex items-center space-x-4 pt-4">
                <Button
                  variant="default"
                  size="default"
                  iconName="Send"
                  iconPosition="left"
                  onClick={() => console.log('Submit ticket')}
                >
                  Submit Ticket
                </Button>
                <Button
                  variant="ghost"
                  size="default"
                  onClick={() => {
                    setTicketSubject('');
                    setTicketCategory('');
                  }}
                >
                  Clear Form
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border p-8">
            <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
              <Icon name="Ticket" size={24} className="mr-3" />
              Recent Tickets
            </h3>
            
            <div className="space-y-4">
              {recentTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="bg-muted/50 rounded-xl p-4 hover:bg-muted transition-colors duration-300 cursor-pointer"
                  onClick={() => console.log(`Opening ticket: ${ticket.id}`)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-mono text-text-secondary">
                          {ticket.id}
                        </span>
                        <div className={`px-2 py-0.5 rounded-full border text-xs font-medium ${getStatusColor(ticket.status)}`}>
                          {ticket.status.replace('-', ' ')}
                        </div>
                      </div>
                      <h4 className="font-semibold text-foreground mb-1">
                        {ticket.subject}
                      </h4>
                      <div className="flex items-center space-x-3 text-xs text-text-secondary">
                        <span>{ticket.category}</span>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <Icon name="Flag" size={12} className={getPriorityColor(ticket.priority)} />
                          <span className={getPriorityColor(ticket.priority)}>
                            {ticket.priority}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-text-secondary pt-3 border-t border-border">
                    <span>Created: {ticket.createdAt}</span>
                    <span>Updated: {ticket.lastUpdate}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <Button
              variant="outline"
              size="default"
              fullWidth
              className="mt-6"
              onClick={() => console.log('View all tickets')}
            >
              View All Tickets
            </Button>
          </div>
        </div>

        <div className="mt-12 bg-gradient-to-br from-accent/10 to-accent/5 rounded-2xl border border-accent/20 p-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Premium Support Plans
              </h3>
              <p className="text-text-secondary mb-6">
                Upgrade to premium support for priority assistance, dedicated account management, and guaranteed response times. Perfect for mission-critical deployments.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">Priority ticket routing and faster response times</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">Dedicated technical account manager</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">Quarterly business reviews and optimization sessions</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">Early access to new features and beta programs</span>
                </div>
              </div>
              <Button
                variant="default"
                size="lg"
                iconName="Crown"
                iconPosition="left"
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
                onClick={() => console.log('Learn about premium support')}
              >
                Learn About Premium Support
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-background/50 backdrop-blur-sm rounded-xl p-6 text-center">
                <Icon name="Clock" size={32} className="text-accent mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">&lt;15min</div>
                <div className="text-sm text-text-secondary">Response Time</div>
              </div>
              <div className="bg-background/50 backdrop-blur-sm rounded-xl p-6 text-center">
                <Icon name="Users" size={32} className="text-accent mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">24/7</div>
                <div className="text-sm text-text-secondary">Expert Availability</div>
              </div>
              <div className="bg-background/50 backdrop-blur-sm rounded-xl p-6 text-center">
                <Icon name="Award" size={32} className="text-accent mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">99.9%</div>
                <div className="text-sm text-text-secondary">Resolution Rate</div>
              </div>
              <div className="bg-background/50 backdrop-blur-sm rounded-xl p-6 text-center">
                <Icon name="Star" size={32} className="text-accent mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">4.9/5</div>
                <div className="text-sm text-text-secondary">Customer Rating</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SupportResources;